import { useMemo, useState } from 'react'
import { CardDragHandle } from '@/shared/components/card-drag-handle'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { TestTube, Package, MapPin, Calendar, Plus } from 'lucide-react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import { WorkflowActionMenu } from '@/shared/components/workflows'
import { StatusBadge } from '@/admin/components/shared/status-badge'
import { ResourceLookupContextProvider } from '@/shared/components/ui/resource-lookup-context'
import { useResourceLookupProviders } from '@/admin/hooks/use-resource-lookup-providers'
import { useFormatters } from '@/shared/lib/formatting'
import { useWorkflowInstanceLookup } from '@/shared/hooks/use-workflows'
import {
  useLabSamples,
  useCreateLabSample,
  type LabSample,
} from '@/admin/hooks/use-lab-tasks'

// The sample lifecycle (Requested -> Received -> Reviewed) is a generic
// workflow. State labels/colours and action labels come from the
// ``sample_lifecycle`` workflow definition via the shared workflow kit -
// this component holds no hardcoded status map or transition table.
const WORKFLOW_KEY = 'sample_lifecycle'

interface Props {
  claimUuid: string
  taskUuid?: string | null
  readOnly?: boolean
  /**
   * WPS-151 retest (Penny) C13: curated plant options for the Add Sample
   * popup's Plant Code dropdown, sourced from the claim schema (data). Undefined
   * for a schema with no plant options - the popup then keeps a free-text field.
   */
  plantCodeOptions?: { value: string; label: string }[]
}

export function SampleTrackingCard({
  claimUuid,
  taskUuid,
  readOnly = false,
  plantCodeOptions,
}: Props) {
  const { t } = useTranslation()
  const { data: samples = [], isPending } = useLabSamples(claimUuid)
  const createSample = useCreateLabSample(claimUuid)
  const [addOpen, setAddOpen] = useState(false)

  return (
    <div className="rounded-2xl border border-[var(--rule)] bg-[var(--bg)] shadow-[var(--shadow-card)]">
      {/* Header - the shared card header: icon chip and a plain title */}
      <div className="flex h-[60px] items-center justify-between rounded-t-2xl border-b border-b-[var(--rule-light)] bg-[var(--panel)] px-5">
        <div className="flex items-center gap-3">
          <CardDragHandle />
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[color-mix(in_srgb,var(--green)_10%,transparent)] text-[var(--green)]">
            <TestTube className="h-4 w-4" />
          </span>
          <span className="text-[16px] font-medium text-[var(--ink)]">
            {t('claims.sampleTracking.title', 'Sample Tracking')}
          </span>
          {samples.length > 0 && (
            <Badge variant="outline" className="text-[13px]">
              {samples.length}
            </Badge>
          )}
        </div>
        {!readOnly && (
          <Button
            variant="ghost"
            size="xs"
            onClick={() => setAddOpen(true)}
            className="h-9 rounded-full px-3.5 text-[14px] font-medium text-[var(--green)]"
          >
            <Plus className="mr-1 h-3 w-3" />
            {t('claims.sampleTracking.addSample', 'Add Sample')}
          </Button>
        )}
      </div>

      {/* Samples list - loading gate before empty state (empty-state rule) */}
      {samples.length === 0 ? (
        <div className="border-t-[0.5px] border-[var(--rule)] px-3.5 py-4 text-center text-[11px] text-[var(--ink-3)]">
          {isPending
            ? t('common.loading', 'Loading…')
            : t('claims.sampleTracking.empty', 'No samples tracked yet')}
        </div>
      ) : (
        <div className="border-t-[0.5px] border-[var(--rule)]">
          {samples.map((sample) => (
            <SampleRow
              key={sample.uuid}
              sample={sample}
              claimUuid={claimUuid}
              readOnly={readOnly}
            />
          ))}
        </div>
      )}

      {/* Add sample dialog */}
      {addOpen && (
        <AddSampleDialog
          onClose={() => setAddOpen(false)}
          onSubmit={(data) => {
            createSample.mutate(
              { ...data, ...(taskUuid ? { lab_task_uuid: taskUuid } : {}) },
              { onSuccess: () => setAddOpen(false) }
            )
          }}
          isPending={createSample.isPending}
          plantCodeOptions={plantCodeOptions}
        />
      )}
    </div>
  )
}

function SampleRow({
  sample,
  claimUuid,
  readOnly,
}: {
  sample: LabSample
  claimUuid: string
  readOnly: boolean
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  // The workflow instance is auto-created by the engine's bootstrap signal
  // when the sample row is saved, so a freshly added sample resolves here
  // on the next fetch.
  const { data: instance } = useWorkflowInstanceLookup('lab_sample', sample.uuid)

  const handleChanged = () => {
    qc.invalidateQueries({ queryKey: ['lab-samples', claimUuid] })
    qc.invalidateQueries({ queryKey: ['claim', claimUuid] })
    // WPS-151: the status pill reads the workflow instance's current state,
    // so without this the row still said "Requested" straight after Mark
    // Reviewed and only corrected on a full page reload.
    qc.invalidateQueries({
      queryKey: ['workflows', 'lookup', 'lab_sample', sample.uuid],
    })
  }

  const state = instance?.current_state
  const { formatDate } = useFormatters()

  // WPS-151: the sample's status and the date it reached that status read as
  // one line. Each lifecycle milestone that has a date is shown as
  // "<label> <date>"; the one matching the current workflow state is
  // emphasised so the status and its date are a single, glanceable unit,
  // while earlier milestones stay muted as history. A sample still in
  // "Requested" has no date yet, so only the pill shows.
  const currentLabel = (state?.label ?? sample.status ?? '').toLowerCase()
  const milestones = [
    {
      key: 'received',
      label: t('claims.sampleTracking.received', 'Received'),
      date: sample.received_date ?? '',
      match: 'receiv',
    },
    {
      key: 'reviewed',
      label: t('claims.sampleTracking.reviewed', 'Reviewed'),
      date: sample.reviewed_date ?? '',
      match: 'review',
    },
  ].filter((m) => m.date !== '')
  const activeIdx = (() => {
    const byLabel = milestones.findIndex((m) => currentLabel.includes(m.match))
    return byLabel !== -1 ? byLabel : milestones.length - 1
  })()

  // Identity facts (what the sample IS), kept muted below the status line.
  // WPS-151 retest: production is a freeform code again; older rows logged
  // during the date-picker pass fall back to the formatted production_date.
  const production = sample.production_code
    ? sample.production_code
    : sample.production_date
      ? formatDate(sample.production_date)
      : ''
  const identityBits: string[] = []
  if (sample.plant_code)
    identityBits.push(
      `${t('claims.sampleTracking.plant', 'Plant')} ${sample.plant_code}`,
    )
  if (production)
    identityBits.push(
      `${t('claims.sampleTracking.production', 'Production')} ${production}`,
    )

  const productName =
    sample.product ||
    sample.sample_id ||
    sample.description ||
    t('claims.sampleTracking.sampleFallback', 'Sample {{id}}', {
      id: sample.uuid.slice(0, 8),
    })

  // The card spans the full claim-detail column, so a single flowing row that
  // uses the width reads far tighter than short text stacked against a void:
  // identity, the status/date timeline, and the sample facts sit left-to-right
  // (wrapping only when they must); the status pill and any workflow action
  // anchor the right edge.
  return (
    <div className="flex items-center justify-between gap-x-4 gap-y-2 border-b-[0.5px] border-[var(--rule)] px-3.5 py-2.5 last:border-b-0">
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
        {/* Identity: Product names the sample now that the popup no longer
            captures a Sample ID; legacy rows fall back to sample_id /
            description, then the uuid stub. */}
        <span className="flex min-w-0 items-center gap-1.5">
          <Package className="h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
          <span className="truncate text-[12px] font-medium text-[var(--ink)]">
            {productName}
          </span>
        </span>

        {/* Status/date timeline - the unified "Received <date> · Reviewed
            <date>" run, current milestone emphasised. */}
        {milestones.length > 0 && (
          <span className="flex items-center gap-x-1.5">
            <Calendar className="h-3 w-3 shrink-0 text-[var(--ink-3)]" />
            {milestones.map((m, i) => (
              <span key={m.key} className="flex items-center gap-x-1.5">
                {i > 0 && <span className="text-[var(--ink-3)]">·</span>}
                <span
                  className={
                    i === activeIdx
                      ? 'font-medium text-[var(--ink)]'
                      : 'text-[var(--ink-3)]'
                  }
                >
                  {m.label}{' '}
                  <span className="tabular-nums">{formatDate(m.date)}</span>
                </span>
              </span>
            ))}
          </span>
        )}

        {/* Sample facts, muted. */}
        {identityBits.length > 0 && (
          <span className="text-[var(--ink-3)]">
            {identityBits.join('  ·  ')}
          </span>
        )}
        {sample.location && (
          <span className="flex items-center gap-1.5 text-[var(--ink-3)]">
            <MapPin className="h-3 w-3 shrink-0" />
            {sample.location}
          </span>
        )}
      </div>

      {/* Status + workflow action (Mark Received / Mark Reviewed), right-anchored.
          The pill uses the same crisp semantic colour every other status pill in
          the app uses, driven by the workflow state label. */}
      <div className="flex shrink-0 items-center gap-2">
        <StatusBadge status={state?.label ?? sample.status} color={state?.color} />
        {!readOnly && instance && (
          <WorkflowActionMenu
            instanceUuid={instance.uuid}
            workflowKey={WORKFLOW_KEY}
            buttonSize="xs"
            onActionExecuted={handleChanged}
          />
        )}
      </div>
    </div>
  )
}

function AddSampleDialog({
  onClose,
  onSubmit,
  isPending,
  plantCodeOptions,
}: {
  onClose: () => void
  onSubmit: (data: Record<string, unknown>) => void
  isPending: boolean
  plantCodeOptions?: { value: string; label: string }[]
}) {
  const { t } = useTranslation()
  const resourceLookupProviders = useResourceLookupProviders()
  // WPS-58: a sample is created in "Requested".
  // WPS-151: the popup captures Product plus the plant / production codes
  // read off the wrapper. Sample ID is gone (the lab identifies a sample by
  // its claim + product, not a separate tracking number) and Description is
  // replaced by Product. Plant / production capture stays here (Mark Reviewed
  // now captures only the Reviewed Date).
  const fields = useMemo<FormField[]>(
    () => [
      {
        // WPS-151 retest: Product is a product-model picker (same catalogue the
        // claim's "Product Name" uses), so the lab's identified product carries
        // a resolvable id and can be reconciled onto the claim + registration.
        // `allow_create` keeps a free-text fallback for an off-catalogue sample
        // (name-only: it still updates the claim, but not the registration FK).
        type: 'entity-search',
        name: 'product',
        label: t('claims.sampleTracking.product', 'Product'),
        placeholder: t(
          'claims.sampleTracking.productPlaceholder',
          'Product the sample was cut from',
        ),
        value: '',
        required: t(
          'claims.sampleTracking.productRequired',
          'Product is required.',
        ),
        configurationData: {
          lookup_provider: 'product_model',
          allow_create: true,
          icon: 'package',
        },
      },
      // WPS-151 retest (Penny) C13: Plant Code is a curated DROPDOWN of the
      // active roofing plants ("Kearny - 1182"), not a free-text number. The
      // options come from the claim schema (`plantCodeOptions`, data), so this
      // capture surface offers the same list as the Sample Tracking card. A
      // schema with no plant options falls back to the free-text field.
      plantCodeOptions && plantCodeOptions.length > 0
        ? {
            type: 'select' as const,
            name: 'plant_code',
            label: t('claims.sampleTracking.plantCode', 'Plant Code'),
            placeholder: t(
              'claims.sampleTracking.plantCodeSelect',
              'Select a plant',
            ),
            value: '',
            options: plantCodeOptions,
          }
        : {
            type: 'text' as const,
            name: 'plant_code',
            label: t('claims.sampleTracking.plantCode', 'Plant Code'),
            placeholder: t(
              'claims.sampleTracking.plantCodePlaceholder',
              'e.g. 32',
            ),
            value: '',
          },
      {
        // WPS-151 retest (Penny): the production value read off the wrapper is a
        // code, not a calendar date - a plain freeform text field, no picker.
        type: 'text',
        name: 'production_code',
        label: t('claims.sampleTracking.productionCode', 'Production Code'),
        placeholder: t(
          'claims.sampleTracking.productionCodePlaceholder',
          'e.g. SUM026',
        ),
        value: '',
      },
    ],
    [t, plantCodeOptions],
  )

  const { Form, ref } = useForm(
    {
      name: 'add-lab-sample-form',
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = rawValues as unknown as {
        product?: { uuid?: string; name?: string } | string
        plant_code?: string
        production_code?: string
      }
      // The product picker yields `{ uuid, name }`; a free-text "Add ..." pick
      // carries a blank uuid. Send the name for the sample's own column and the
      // uuid alongside so the backend can reconcile the claim + registration.
      const picked = values.product
      const productName =
        typeof picked === 'string' ? picked : (picked?.name ?? '')
      const productUuid =
        typeof picked === 'string' ? '' : (picked?.uuid ?? '')
      onSubmit({
        product: productName.trim(),
        product_uuid: productUuid,
        plant_code: (values.plant_code ?? '').trim(),
        production_code: (values.production_code ?? '').trim(),
      })
    },
    [onSubmit],
  )

  return (
    // Guarded while a create is in flight: closing mid-submit would leave the
    // list stale and invites a duplicate on reopen.
    <Dialog open onOpenChange={isPending ? undefined : onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('claims.sampleTracking.addSample', 'Add Sample')}
          </DialogTitle>
        </DialogHeader>
        {/* Plant Code / Production Code sit together - they are read off the
            same wrapper stamp, so they read as one pair on the form. The
            provider lets the Product picker resolve the product-model catalogue
            (this dialog lives outside the entity-action-menu tree). */}
        <ResourceLookupContextProvider providers={resourceLookupProviders}>
          <Form className="flex flex-col gap-3" />
        </ResourceLookupContextProvider>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              ref.current?.resetForm()
              onClose()
            }}
            disabled={isPending}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            type="submit"
            form="add-lab-sample-form"
            disabled={isPending}
          >
            {isPending
              ? t('claims.sampleTracking.creating', 'Creating...')
              : t('claims.sampleTracking.addSample', 'Add Sample')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
